import { Platform, SourceType } from '@foundation-player/runtime';
import { TransformPluginConfigs } from './transform-plugin-configs';

/**
 * This function is used to evaluate the plugin configuration for the Entitlement Plugin.
 *
 * @remarks
 * The plugin is enabled via the remote configuration for applicable clients.
 */
export const addEntitlementPlugin: TransformPluginConfigs = (
  partialBuilderConfig,
  pluginConfigs,
  _clientAndScope,
  _environment,
  platform,
) => {
  const entitlement = 'foundation.plugin.entitlement';
  const { remoteConfig } = partialBuilderConfig;

  const enabled =
    // Only use the RemoteConfig if it's actually set
    typeof pluginConfigs[entitlement]?.enabled === 'boolean'
      ? pluginConfigs[entitlement].enabled
      : remoteConfig.entitlement?.vod?.check?.enable ||
        remoteConfig.entitlement?.livestream?.check?.enable ||
        remoteConfig.entitlement?.liveevent?.check?.enable;

  if (!enabled) {
    return pluginConfigs;
  }

  const headers = pluginConfigs[entitlement]?.headers || {};
  headers['X-Device-Type'] =
    pluginConfigs[entitlement]?.headers?.['X-Device-Type'] || mapPlatformToDeviceType(platform);

  return {
    ...pluginConfigs,
    [entitlement]: {
      ...pluginConfigs[entitlement],
      headers,
      endpoints: {
        [SourceType.VOD]: remoteConfig.entitlement?.vod?.check?.enable
          ? remoteConfig.entitlement.vod.check.endpoint
          : undefined,
        [SourceType.Live]: remoteConfig.entitlement?.livestream?.check?.enable
          ? remoteConfig.entitlement.livestream.check.endpoint
          : undefined,
        [SourceType.Event]: remoteConfig.entitlement?.liveevent?.check?.enable
          ? remoteConfig.entitlement.liveevent.check.endpoint
          : undefined,
        // NOTE: Currently we don't want geoblocking for SourceType.FAST.
        // @See https://rtltech.atlassian.net/browse/FOUN-3005
        ...pluginConfigs[entitlement]?.endpoints,
      },
      enabled: true,
    },
  };
};

/**
 * For PLAYHTML5-702 we also set a X-Device-Type header for the entitlement request.
 * According to the ticket the value should be the same as the "platform" value for STUS.
 * This value is inferred on our side from our platform value.
 *
 * This list is copied from our STUS logic, but kept separate on purpose due to the lack of
 * a concrete requirement definition for this in the ticket.
 */
function mapPlatformToDeviceType(platform: Platform | undefined): string {
  switch (platform) {
    case Platform.WebOS3:
    case Platform.WebOS4:
    case Platform.WebOS5:
    case Platform.WebOS6:
    case Platform.WebOS22:
    case Platform.WebOS23:
    case Platform.WebOS24:
    case Platform.WebOS25:
      return 'lg';
    case Platform.Tizen3:
    case Platform.Tizen4:
    case Platform.Tizen5:
    case Platform.Tizen5_5:
    case Platform.Tizen6:
    case Platform.Tizen6_5:
    case Platform.Tizen7:
    case Platform.Tizen8:
      return 'tizen';
    case Platform.MagentaG3:
    case Platform.MagentaG4:
    case Platform.MagentaG5:
      return 'magenta';
    case Platform.SkyQ:
      return 'skyq';
    case Platform.SkyXiOne:
      return 'skyxi1';
    case Platform.SkyStream:
      return 'skystream';
    case Platform.VodafoneGTV:
      return 'vodafone-gtv';
    case Platform.VodafoneVTV:
      return 'vodafone-vtv';
    case Platform.TeleColumbus:
      return 'tc';
    case Platform.Kepler:
      // There are two versions for Kepler defined in StUS, which are 'firern' and 'fireweb'.
      // These define the react native and webview variants respectively
      // Since we only support the webview variant, we use 'fireweb' here.
      return 'fireweb';
    default:
      return 'web';
  }
}
